import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IAPIResponse } from '../../shared/models/IApiResponse';
import { ICustomReportSettings } from '../models/ICustomReportSettings';
import { IAreaDate } from '../models/IAreaDate';
import { ReportPeriod } from '../../customer-shared/models/ReportPeriod';
import { IGlimpseReport, IInsightItem } from '../../customer-shared/models/IGlimpseReport';
import { MonthlyReportInsightItems } from '../models/MonthlyReportInsightItems';
import { AnonymousUser } from '../models/AnonymousUser';
import { ExtraCameraImage } from '../models/ExtraCameraImage';
import { PeopleCounter } from '../models/PeopleCounter';
import { PosCategorySop } from "../../customer-shared/models/PosCategorySop";
import { IAuditSales } from "../../shared/models/IAuditSales";

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(private http: HttpClient) {}

  getReport(areaId: number, periodId: string, isPreview: boolean = false): Observable<IAPIResponse<IGlimpseReport>> {
    const url = isPreview
      ? `${environment.apiBase}/v1.0/publish/preview/${areaId}/${periodId}`
      : `${environment.apiBase}/v1.0/reports/default/${areaId}/${periodId}`;

    return this.http.get<IAPIResponse<IGlimpseReport>>(url);
  }

  getCustomReport(
    areaId: number,
    periodId: string,
    settings: Partial<ICustomReportSettings>,
    isPreview: boolean = false,
  ): Observable<IAPIResponse<IGlimpseReport>> {
    const url = isPreview
      ? `${environment.apiBase}/v1.0/publish/preview/custom/${areaId}/${periodId}`
      : `${environment.apiBase}/v1.0/reports/custom/${areaId}/${periodId}`;

    return this.http.post<IAPIResponse<IGlimpseReport>>(url, settings);
  }

  getAreaAuditDates(areaId: number): Observable<IAPIResponse<IAreaDate[]>> {
    return this.http.get<IAPIResponse<IAreaDate[]>>(`${environment.apiBase}/v1.0/reports/area/${areaId}/period/${ReportPeriod.Daily}`);
  }

  getAreaAuditMonths(areaId: number, months: number = 240): Observable<IAPIResponse<IAreaDate[]>> {
    return this.http.get<IAPIResponse<IAreaDate[]>>(`${environment.apiBase}/v1.0/reports/area/${areaId}/period/${ReportPeriod.Monthly}`);
  }

  getAuditSalesData(areaId: number, periodId: string): Observable<IAuditSales> {
    return this.http.get<IAuditSales>(`${environment.apiBase}/v1.0/customers/sales/${areaId}/${periodId}`);
  }

  getReportByToken(token: string): Observable<IAPIResponse<{ report: IGlimpseReport; user: AnonymousUser }>> {
    return this.http.get<IAPIResponse<{ report: IGlimpseReport; user: AnonymousUser }>>(
      `${environment.apiBase}/v1.0/reports/default/${token}`,
    );
  }

  getMonthlyInsights(areaId: number, periodId: string, token?: string): Observable<MonthlyReportInsightItems[]> {
    return token
      ? this.http.get<MonthlyReportInsightItems[]>(`${environment.apiBase}/v1.0/reports/insights/monthly/${areaId}/${token}`)
      : this.http.get<MonthlyReportInsightItems[]>(`${environment.apiBase}/v1.0/reports/insights/${areaId}/${periodId}`);
  }

  /**
   * @deprecated Used to get data manually generated for Subway
   * @deprecated Use `getPeopleCount` instead
   * @param periodId
   * @param cameraId
   */
  getPeopleInStore(periodId: string, cameraId: number) {
    return this.http.get<{
      [timestamp: string]:
        | {
            count: number;
            image: string;
          }
        | number;
    }>(`https://subwayinference.blob.core.windows.net/people-count/${periodId}_${cameraId}.json`);
  }

  getPeopleCount(areaId: number, periodId: string) {
    return this.http.get<PeopleCounter>(`${environment.apiBase}/v1.0/reports/people-count/${areaId}/${periodId}`);
  }

  getAiInsight(areaId: number, periodId: string, isSpanish: boolean, type: 'ai' | 'trend') {
    let params = new HttpParams().set('isSpanish', isSpanish);
    let url =
      type === 'ai'
        ? `${environment.apiBase}/v1.0/publish/insight/generate/${areaId}/${periodId}`
        : `${environment.apiBase}/v1.0/publish/insight/analytics/generate/${areaId}/${periodId.substring(0, 7)}`;
    return this.http.post<string>(url, null, {
      params,
      responseType: 'text' as 'json',
    });
  }

  regenerateAiInsight(areaId: number, periodId: string, itemId: number): Observable<IInsightItem> {
    return this.http.post<IInsightItem>(`${environment.apiBase}/v1.0/publish/insight/generate/${areaId}/${periodId}/${itemId}`, null);
  }

  /**
   * Frames for a camera around a moment. The audited day is finished by the time anyone browses it,
   * so the same window always answers the same way and is worth holding on to: opening an event,
   * closing it and opening it again asked the server for the same four hours every time. An ANPR
   * event carries only two images of its own, which puts the browser straight into paging on open,
   * so those repeats were the common case rather than the exception.
   *
   * Held per window rather than per frame, since that is the unit the browser asks in. Shared so
   * that two callers arriving together make one request, and replayed so later subscribers get the
   * answer without another.
   */
  getImages(cameraId: number, periodId: string, time: string, before: number, after: number) {
    const key = `${cameraId}|${periodId}|${time}|${before}|${after}`;

    const cached = this.imageWindows.get(key);
    if (cached) {
      return cached;
    }

    const request = this.http
      .get<ExtraCameraImage[]>(`${environment.apiBase}/v1.0/cameras/${cameraId}/frames/${periodId}`, {
        params: {
          time,
          before: before.toString(),
          after: after.toString(),
        },
      })
      .pipe(
        //a failure must not be remembered, or the browser can never recover from one dropped request
        catchError((error: unknown) => {
          this.imageWindows.delete(key);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.imageWindows.set(key, request);

    return request;
  }

  /** Frames already fetched this session, keyed by the window that was asked for. */
  private readonly imageWindows = new Map<string, Observable<ExtraCameraImage[]>>();

  loadPosCategorySop(customerId: number, locationId: number): Observable<PosCategorySop[]> {
    return this.http.get<PosCategorySop[]>(`${environment.apiBase}/v1.0/customers/${customerId}/locations/${locationId}/sop`);
  }

  updatePosCategorySop(customerId: number, locationId: number, sop: PosCategorySop[]): Observable<PosCategorySop[]> {
    let res = sop.map(e => ({
      templateButtonId: e.templateButtonId,
      preSopSeconds: e.preSopSeconds,
      postSopSeconds: e.postSopSeconds,
      areaSops: e.areaSops,
    }));
    return this.http.post<PosCategorySop[]>(`${environment.apiBase}/v1.0/customers/${customerId}/locations/${locationId}/sop`, res);
  }
}
